import { Controller } from "@hotwired/stimulus"
import { Modal } from "bootstrap"

export default class extends Controller {
  static targets = ["dialog", "input", "results", "status", "mode"]
  static values = { url: String }

  connect() {
    this.mode = "services"
    this.modal = Modal.getOrCreateInstance(this.dialogTarget)
    this.onShown = () => this.inputTarget.focus()
    this.onHidden = () => {
      this.setExpanded(false)
      if (this.trigger?.isConnected) this.trigger.focus()
    }
    this.onResize = () => this.position()
    this.beforeCache = () => this.closeImmediately()
    this.dialogTarget.addEventListener("shown.bs.modal", this.onShown)
    this.dialogTarget.addEventListener("hidden.bs.modal", this.onHidden)
    window.addEventListener("resize", this.onResize)
    document.addEventListener("turbo:before-cache", this.beforeCache)
  }

  async open(event) {
    this.trigger = event.currentTarget
    this.position()
    this.inputTarget.value = ""
    this.mode = "services"
    this.updateMode()
    this.setExpanded(true)
    this.modal.show(this.trigger)
    if (this.services) return this.search()
    if (this.loading) return

    this.loading = true
    this.statusTarget.textContent = "Загружаем услуги…"
    this.abort = new AbortController()
    try {
      const response = await fetch(this.urlValue, { signal: this.abort.signal, headers: { Accept: "application/json" } })
      if (!response.ok) throw new Error("Search unavailable")
      const catalog = await response.json()
      this.services = catalog.services
      this.categories = catalog.categories
      this.search()
    } catch (error) {
      if (error.name === "AbortError") return
      this.statusTarget.textContent = "Не удалось загрузить услуги. Проверьте соединение и повторите."
      const retry = document.createElement("button")
      retry.type = "button"
      retry.className = "btn btn-outline-primary"
      retry.textContent = "Повторить"
      retry.addEventListener("click", () => this.open({ currentTarget: this.trigger }))
      this.resultsTarget.replaceChildren(retry)
    } finally {
      this.loading = false
    }
  }

  normalize(value) {
    return value.toLocaleLowerCase("ru").replaceAll("ё", "е").trim()
  }

  switchMode(event) {
    this.mode = event.currentTarget.dataset.mode
    this.updateMode()
    this.search()
  }

  updateMode() {
    const categories = this.mode === "categories"
    this.modeTargets.forEach(button => {
      const active = button.dataset.mode === this.mode
      button.classList.toggle("active", active)
      button.setAttribute("aria-pressed", String(active))
    })
    this.inputTarget.placeholder = categories ? "Название категории" : "Например, компрессор или диагностика"
    this.inputTarget.setAttribute("aria-label", categories ? "Поиск категорий" : "Поиск услуг")
    this.resultsTarget.classList.toggle("is-categories", categories)
  }

  search() {
    if (!this.services) return
    const query = this.normalize(this.inputTarget.value)
    const words = query.split(/\s+/).filter(Boolean)
    if (this.mode === "categories") {
      const matches = this.categories.filter(category => words.every(word => this.normalize(category.name).includes(word)))
      this.statusTarget.textContent = query
        ? (matches.length ? `Найдено категорий: ${matches.length}` : "Категории не найдены. Попробуйте другое название.")
        : (matches.length ? "Выберите категорию" : "Доступных категорий пока нет.")
      this.resultsTarget.replaceChildren(...matches.map(category => this.categoryResult(category)))
      return
    }
    const matches = this.services.filter(service => {
      const text = this.normalize(`${service.name} ${service.category}`)
      return words.every(word => text.includes(word))
    }).sort((a, b) => {
      const score = service => (query && this.normalize(service.name).startsWith(query) ? 2 : 0) + (service.popular ? 1 : 0)
      return score(b) - score(a) || a.name.localeCompare(b.name, "ru")
    })
    const displayed = matches.slice(0, query ? 20 : 6)
    this.statusTarget.textContent = query
      ? (matches.length ? `Найдено услуг: ${matches.length}${matches.length > 20 ? ". Уточните запрос, чтобы сузить список." : ""}` : "Ничего не найдено. Попробуйте другое название или категорию.")
      : (matches.length ? "Популярные услуги и быстрые переходы" : "Доступных услуг пока нет.")
    this.resultsTarget.replaceChildren(...displayed.map(service => this.result(service)))
  }

  result(service) {
    const link = document.createElement("a")
    link.href = service.url
    link.className = "service-search-result"
    const text = document.createElement("span")
    text.className = "service-search-result-text"
    const name = document.createElement("strong")
    name.textContent = service.name
    const category = document.createElement("small")
    category.textContent = service.category
    text.append(name, category)
    const price = document.createElement("span")
    price.className = "service-search-price"
    price.textContent = service.available ? (service.price || "Уточнить цену") : "Сейчас недоступна"
    link.append(text, price)
    link.addEventListener("click", () => this.modal.hide())
    return link
  }

  categoryResult(category) {
    const link = document.createElement("a")
    link.href = category.url
    link.className = "service-search-category"
    if (category.image) {
      const image = document.createElement("img")
      image.src = category.image
      image.alt = category.alt
      image.loading = "lazy"
      link.append(image)
    } else {
      const placeholder = document.createElement("span")
      placeholder.className = "service-search-category-placeholder"
      placeholder.textContent = "❄️"
      placeholder.setAttribute("aria-hidden", "true")
      link.append(placeholder)
    }
    const body = document.createElement("span")
    body.className = "service-search-category-body"
    const name = document.createElement("strong")
    name.textContent = category.name
    const count = document.createElement("small")
    count.textContent = `Услуг: ${category.count}`
    body.append(name, count)
    link.append(body)
    link.addEventListener("click", () => this.modal.hide())
    return link
  }

  position() {
    const navbar = document.querySelector(".site-navbar")
    const top = Math.max(0, navbar?.getBoundingClientRect().bottom || 0) + 8
    this.dialogTarget.style.setProperty("--search-top", `${top}px`)
  }

  setExpanded(expanded) {
    document.querySelectorAll("[data-service-search-trigger]").forEach(button => button.setAttribute("aria-expanded", String(expanded)))
  }

  closeImmediately() {
    if (this.dialogTarget.style.display === "block") {
      this.dialogTarget.dispatchEvent(new Event("transitionend"))
      this.modal.hide()
      this.dialogTarget.dispatchEvent(new Event("transitionend"))
      document.querySelectorAll(".modal-backdrop").forEach(element => element.dispatchEvent(new Event("transitionend")))
    }
    this.setExpanded(false)
  }

  disconnect() {
    this.abort?.abort()
    this.closeImmediately()
    this.dialogTarget.removeEventListener("shown.bs.modal", this.onShown)
    this.dialogTarget.removeEventListener("hidden.bs.modal", this.onHidden)
    window.removeEventListener("resize", this.onResize)
    document.removeEventListener("turbo:before-cache", this.beforeCache)
    this.modal.dispose()
  }
}
